import client from "../http-clients/api";
import clientPayment from "../http-clients/api-payment";
import {
  IDiscountItemOutput,
  ICartMessage,
  ICartCheckoutOutput,
  IDiscountRemovedOutput,
  ICheckoutProcessInput,
  ICheckoutProcessOutput,
  ICheckoutDirectPayment,
  ICheckoutDirectContract,
  IGetFactor,
  IOrderDetailOutput,
  IRecommendedCourseCheckout,
  IGetCheckoutOrders,
  IAddPermanentCouponOutput,
  ORDER_COUPON_TYPE,
  ICheckSnapPayOutput,
} from "@types";
import gate from "../gate";

const cartApi = {
  add: (id: number) =>
    gate.post<ICartMessage, never, never>({
      client,
      url: `/cart/add/${id}`,
    }),
  remove: (id: number) =>
    gate.post<ICartMessage, never, never>({
      client,
      url: `/cart/remove/${id}`,
    }),
  getCartItem: () =>
    gate.get<ICartMessage, never, never>({
      client,
      url: `/cart`,
    }),
  getCheckoutDetail: () =>
    gate.get<ICartCheckoutOutput>({ client, url: "/checkout" }),

  apWallet: () =>
    gate.get<number>({
      client,
      url: "/wallet/get/getBalance",
    }),
  addDiscount: (coupon_code: string, is_hash?: boolean) =>
    gate.get<IDiscountItemOutput, never, any>({
      client,
      params: { coupon_code, is_hash },
      url: `/cart/coupon/validate`,
    }),
  removeDiscount: (coupon_code: string) =>
    gate.get<IDiscountRemovedOutput, never, any>({
      client,
      url: `/cart/coupon/delete`,
      params: { coupon_code },
    }),
  registerFree: (id: number) =>
    gate.post<any>({
      client,
      url: `/order/register/${id}`,
    }),
  preRegister: (id: number) =>
    gate.post<any>({
      client,
      url: `/order/pre-register/${id}`,
    }),
  checkout: (data: ICheckoutProcessInput) =>
    gate.post<ICheckoutProcessOutput, ICheckoutProcessInput, never>({
      client,
      url: `/checkout/pay`,
      data,
    }),
  directPayment: () =>
    gate.get<ICheckoutDirectPayment>({
      client,
      url: "/ap/direct/status",
    }),
  directContract: () =>
    gate.post<ICheckoutDirectContract>({
      client,
      url: "/ap/direct/contract",
    }),
  getFactor: (id: string) =>
    gate.post<IGetFactor, { order_id: string }, never>({
      client,
      url: `/order/factor`,
      data: { order_id: id },
    }),
  submitNationalCode: (id: string) =>
    gate.post<ICheckoutDirectPayment, { national_code: string }, never>({
      client,
      url: `/order/factor/nationalCode`,
      data: { national_code: id },
    }),
  submitMobile: (id: string) =>
    gate.post<ICheckoutDirectPayment, { mobile: string }, never>({
      client,
      url: `/order/factor/submit/mobile`,
      data: { mobile: id },
    }),
  verifyMobile: (mobile: string, code: string) =>
    gate.post<ICheckoutDirectPayment, { mobile: string; code: string }, never>({
      client,
      url: `/order/factor/verify/mobile`,
      data: { mobile: mobile, code: code },
    }),
  validateOrder: (
    id: number,
    wtoken: string,
    status: boolean,
    ReqResult: string,
  ) =>
    gate.get<
      IOrderDetailOutput,
      { id?: number; status: boolean; wtoken?: string; ReqResult?: string },
      never
    >({
      client,
      url: `/order/validate${id ? `/${id}` : ""}`,
      data: wtoken !== "" ? { status, wtoken, ReqResult } : { id, status },
    }),
  getRecommendedCourse: (count: number) =>
    gate.get<IRecommendedCourseCheckout, never, { count: number }>({
      client,
      url: `/checkout/recommend`,
      params: { count: count },
    }),
  getOrdersStatus: () =>
    gate.get<IGetCheckoutOrders, never, never>({
      client,
      url: `/checkout/orders`,
    }),
  addCredentialsCheckout: (data: {
    first_name?: string;
    last_name?: string;
    mobile?: string;
  }) =>
    gate.post<
      { status: boolean; message: string },
      { first_name?: string; last_name?: string; mobile?: string },
      never
    >({
      client: client,
      url: `/checkout/credentials`,
      data: data,
    }),
  verifyMobileCheckout: (data: { code: string; mobile: string }) =>
    gate.post<
      { status: boolean; message: string },
      {
        code: string;
        mobile: string;
      },
      never
    >({
      client: client,
      url: `/checkout/otp/validate`,
      data,
    }),
  addPermanentCoupon: (id: string, type: ORDER_COUPON_TYPE) =>
    gate.post<IAddPermanentCouponOutput, never, { type: ORDER_COUPON_TYPE }>({
      client,
      url: `/cart/permanent/add/${id}`,
      params: {
        type,
      },
    }),
  checkSnapPay: (amount: number) =>
    gate.post<ICheckSnapPayOutput, { amount: number; gateway: string }, never>({
      client: clientPayment,
      url: `/payment/check-eligibility`,
      data: {
        amount: amount,
        gateway: "snappay",
      },
    }),
};

export default cartApi;
